import { useEffect, useState } from 'react'
import { Controller, FormProvider, type Path, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  Grid,
  Stack,
  styled,
  Switch,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { makeZodI18nMap } from 'zod-i18n-map'
import { HookFormTextField } from '@portal/pkg-hook-form'
import { SideDrawer } from '@portal/pkg-ui'
import type { OrganizationDetailResponse } from '@portal/pkg-api/gen/admin-console'
import { useCreateOrg, useOrgDetails, useUpdateOrg } from '../../organization'
import { OrgAdminSchema, OrgAdminFormValues, orgAdminDefaults } from '../model/orgAdminSchema'
import { toCreateRequest, toUpdateRequest, type SecretFieldName } from './OrgAdminForm.requests'
import {
  isDuplicateOrganization,
  isInvalidDbtJobId,
  isUserLimitBelowActiveUsers,
} from '../problems'

interface OrgAdminFormProps {
  onClose: VoidFunction
  orgId?: number
}

const Root = styled('form', {
  name: 'OrgAdminForm',
  slot: 'Root',
})(({ theme }) => ({
  '& .MuiFormLabel-root': {
    color: theme.palette.text.primary,
  },
  '& .MuiInputBase-root': {
    backgroundColor: theme.palette.background.paper,
  },
}))

export function OrgAdminForm({ onClose, orgId }: OrgAdminFormProps) {
  const { t } = useTranslation('admin-console')
  const { enqueueSnackbar } = useSnackbar()
  const isEdit = orgId !== undefined
  const createOrg = useCreateOrg()
  const updateOrg = useUpdateOrg()
  const orgDetails = useOrgDetails(orgId)
  const [clearedSecrets, setClearedSecrets] = useState<Set<SecretFieldName>>(
    () => new Set<SecretFieldName>(),
  )

  const form = useForm<OrgAdminFormValues>({
    mode: 'onTouched',
    resolver: zodResolver(OrgAdminSchema, {
      errorMap: makeZodI18nMap({ handlePath: false }),
    }),
    defaultValues: orgAdminDefaults,
  })

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { isValid, isDirty, dirtyFields },
  } = form

  useEffect(() => {
    if (isEdit && orgDetails.data && 'name' in orgDetails.data) {
      reset(toFormValues(orgDetails.data))
    }
  }, [isEdit, orgDetails.data, reset])

  const clearSecret = (name: SecretFieldName) => {
    setValue(name, '', {
      shouldTouch: true,
      shouldValidate: true,
    })
    setClearedSecrets((current) => new Set(current).add(name))
  }

  const onSubmit = (values: OrgAdminFormValues) => {
    const onSuccess = () => {
      enqueueSnackbar(t(isEdit ? 'users.orgForm.updated' : 'users.orgForm.created'), {
        variant: 'success',
      })
      onClose()
    }
    const onError = (error: unknown) => {
      if (isDuplicateOrganization(error)) {
        enqueueSnackbar(t('users.orgForm.duplicateUniqueId'), { variant: 'error' })
      } else if (isInvalidDbtJobId(error)) {
        enqueueSnackbar(t('users.orgForm.invalidDbtJobId'), { variant: 'error' })
      } else if (isUserLimitBelowActiveUsers(error)) {
        enqueueSnackbar(t('users.orgForm.userLimitBelowActiveUsers'), { variant: 'error' })
      } else {
        enqueueSnackbar(t(isEdit ? 'users.orgForm.updateFailed' : 'users.orgForm.saveFailed'), {
          variant: 'error',
        })
      }
    }

    if (isEdit) {
      updateOrg.mutate(
        {
          id: orgId,
          ...toUpdateRequest(values, dirtyFields, clearedSecrets),
        },
        { onSuccess, onError },
      )
    } else {
      createOrg.mutate(toCreateRequest(values), { onSuccess, onError })
    }
  }

  return (
    <SideDrawer
      title={isEdit ? t('users.orgForm.editTitle', { orgId }) : t('users.orgForm.title')}
      onClose={onClose}
      actions={
        <Stack direction="row" spacing={1}>
          <Button color="secondary" onClick={onClose}>
            {t('users.drawer.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={
              !isValid ||
              (isEdit
                ? (!isDirty && clearedSecrets.size === 0) || updateOrg.isPending
                : createOrg.isPending)
            }
            onClick={() => void handleSubmit(onSubmit)()}
          >
            {t('users.drawer.save')}
          </Button>
        </Stack>
      }
    >
      <FormProvider {...form}>
        {isEdit && orgDetails.isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : isEdit && orgDetails.isError ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void orgDetails.refetch()}>
                {t('users.orgForm.retry')}
              </Button>
            }
          >
            {t('users.orgForm.detailsLoadFailed')}
          </Alert>
        ) : (
          <Root noValidate>
            <Stack spacing={2}>
              <OrganizationSectionTitle>
                {t('users.orgForm.sections.details')}
              </OrganizationSectionTitle>
              <Grid container columnSpacing={2} rowSpacing={2}>
                <Grid size={{ xs: 6 }}>
                  <OrganizationField
                    name="orgName"
                    label={t('users.orgForm.orgName')}
                    required
                    disabled={isEdit}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <OrganizationField
                    name="powerUser"
                    label={t('users.orgForm.maxUsers')}
                    type="number"
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="integrationsEnabled"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        sx={{ ml: 0, gap: 0.5 }}
                        label={t('users.orgForm.integrationsEnabled')}
                        labelPlacement="start"
                        control={
                          <Switch
                            size="small"
                            checked={field.value}
                            onChange={(_, checked) => field.onChange(checked)}
                          />
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="entityTypeScopedAuthorizationEnabled"
                    control={control}
                    render={({ field }) => (
                      <FormControlLabel
                        sx={{ ml: 0, gap: 0.5 }}
                        label={t('users.orgForm.entityTypeScopedAuthorizationEnabled')}
                        labelPlacement="start"
                        control={
                          <Switch
                            size="small"
                            checked={field.value}
                            onChange={(_, checked) => field.onChange(checked)}
                          />
                        }
                      />
                    )}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', ml: 2 }}
                  >
                    {t('users.orgForm.entityTypeScopedAuthorizationTiming')}
                  </Typography>
                </Grid>
              </Grid>

              <Divider />
              <OrganizationSectionTitle>{t('users.orgForm.sections.fms')}</OrganizationSectionTitle>
              <Grid container columnSpacing={2} rowSpacing={2}>
                <Grid size={{ xs: 6 }}>
                  <SecretOrganizationField
                    name="oauthAccessKey"
                    label={t('users.orgForm.fmsAccessKey')}
                    isEdit={isEdit}
                    populated={Boolean(orgDetails.data?.oauthAccessKeyPopulated)}
                    onClear={clearSecret}
                  />
                </Grid>
              </Grid>

              <Divider />
              <OrganizationSectionTitle>{t('users.orgForm.sections.dbt')}</OrganizationSectionTitle>
              <Grid container columnSpacing={2} rowSpacing={2}>
                <Grid size={{ xs: 6 }}>
                  <SecretOrganizationField
                    name="dbtSecretKey"
                    label={t('users.orgForm.dbtSecretKey')}
                    isEdit={isEdit}
                    populated={Boolean(orgDetails.data?.dbtSecretKeyPopulated)}
                    onClear={clearSecret}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <SecretOrganizationField
                    name="dbtWebhookKey"
                    label={t('users.orgForm.dbtWebhookKey')}
                    isEdit={isEdit}
                    populated={Boolean(orgDetails.data?.dbtWebhookKeyPopulated)}
                    onClear={clearSecret}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <OrganizationField name="dbtAccountId" label={t('users.orgForm.dbtAccountId')} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <OrganizationField name="dbtJobId" label={t('users.orgForm.dbtJobId')} />
                </Grid>
              </Grid>

              <Divider />
              <OrganizationSectionTitle>{t('users.orgForm.sections.pbi')}</OrganizationSectionTitle>
              <Grid container columnSpacing={2} rowSpacing={2}>
                <Grid size={{ xs: 6 }}>
                  <OrganizationField
                    name="powerBiWorkspaceId"
                    label={t('users.orgForm.pbiWorkspaceId')}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <OrganizationField
                    name="powerBiTenantId"
                    label={t('users.orgForm.pbiTenantId')}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <OrganizationField
                    name="powerBiClientId"
                    label={t('users.orgForm.pbiClientId')}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <SecretOrganizationField
                    name="powerBiClientSecretId"
                    label={t('users.orgForm.pbiClientSecretId')}
                    isEdit={isEdit}
                    populated={Boolean(orgDetails.data?.powerBiClientSecretIdPopulated)}
                    onClear={clearSecret}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <OrganizationField
                    name="orgUniqueId"
                    label={t('users.orgForm.uniqueId')}
                    required
                    disabled={isEdit}
                  />
                </Grid>
              </Grid>
            </Stack>
          </Root>
        )}
      </FormProvider>
    </SideDrawer>
  )
}

function OrganizationSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      sx={{
        color: 'text.disabled',
        fontSize: '0.7rem',
        fontWeight: 700,
        letterSpacing: '0.2em',
        lineHeight: 1,
      }}
    >
      {children}
    </Typography>
  )
}

interface OrganizationFieldProps {
  name: Path<OrgAdminFormValues>
  label: string
  type?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  populated?: boolean
  masked?: boolean
  onClear?: VoidFunction
}

interface SecretOrganizationFieldProps {
  name: SecretFieldName
  label: string
  isEdit: boolean
  populated: boolean
  onClear: (name: SecretFieldName) => void
}

function SecretOrganizationField({
  name,
  label,
  isEdit,
  populated,
  onClear,
}: SecretOrganizationFieldProps) {
  return (
    <OrganizationField
      name={name}
      label={label}
      populated={isEdit && populated}
      masked={isEdit}
      onClear={isEdit ? () => onClear(name) : undefined}
    />
  )
}

function OrganizationField({
  name,
  label,
  type = 'text',
  placeholder,
  required = false,
  disabled = false,
  populated = false,
  masked = false,
  onClear,
}: OrganizationFieldProps) {
  const { t } = useTranslation('admin-console')

  return (
    <HookFormTextField<OrgAdminFormValues>
      name={name}
      label={label}
      type={masked ? 'password' : type}
      placeholder={masked && populated ? t('users.orgForm.maskedValue') : placeholder}
      variant="outlined"
      fullWidth
      size="small"
      required={required}
      disabled={disabled}
      helperText={
        populated ? (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <span>{t('users.orgForm.valueSaved')}</span>
            {onClear && (
              <Button type="button" size="small" onClick={onClear}>
                {t('users.orgForm.clearSavedValue')}
              </Button>
            )}
          </Box>
        ) : undefined
      }
      slotProps={{ inputLabel: { shrink: true } }}
      sx={{
        '& .MuiOutlinedInput-root': {
          height: 40,
          borderRadius: '4px',
        },
        '& .MuiInputBase-input': {
          padding: '9px 12px',
        },
        '& .MuiInputLabel-root': {
          color: 'text.secondary',
        },
      }}
    />
  )
}

function toFormValues(org: OrganizationDetailResponse): OrgAdminFormValues {
  return {
    ...orgAdminDefaults,
    orgName: org.name,
    orgUniqueId: org.uniqueId ?? '',
    description: org.description ?? '',
    powerUser: org.powerUserLimit ?? 0,
    integrationsEnabled: org.integrationsEnabled,
    entityTypeScopedAuthorizationEnabled: org.entityTypeScopedAuthorizationEnabled,
    powerBiWorkspaceId: org.powerBiWorkspaceId ?? '',
    powerBiTenantId: org.powerBiTenantId ?? '',
    powerBiClientId: org.powerBiClientId ?? '',
    dbtAccountId: org.dbtAccountId ?? '',
    dbtJobId: org.dbtJobId ?? '',
  }
}
